import {
  useContext,
  useState,
  createContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useDebounce } from 'use-debounce';
import { useNFTContext } from './NFTContext';
import { SearchFilter } from '../types/search';
import { useRoomContext } from './RoomContext';
import { RoomObjectType } from '../types/room';

interface SearchContextType {
  filters: SearchFilter;
  setFilters: (f: SearchFilter) => void;
  searchTerm: string;
  isFiltered: boolean;
  setSearchTerm: (t: string) => void;
  searchFetching: boolean;
  suggestedRooms: RoomObjectType[];
}

const SearchContext = createContext<SearchContextType>({
  filters: {
    floorNumber: undefined,
    lockedState: undefined,
    visitorLimit: undefined,
    size: undefined,
    search: undefined,
  },
  setFilters: () => {},
  searchTerm: '',
  isFiltered: false,
  setSearchTerm: () => {},
  searchFetching: false,
  suggestedRooms: [],
});

const requiredAttributes = [
  'roomId',
  'roomName',
  'displayName',
  'contractAddress',
  'tokenId',
  'imageUrl',
  'isPlaced',
  'startX',
  'endX',
  'startY',
  'endY',
  'roomFloor',
  'roomTypeId',
  'roomDescription',
  'roomVisitorLimit',
  'roomLockedState',
  'mapIconSize',
  'mapIconUrl',
  'mapIconStatus',
].join(',');

export function SearchContextProvider({ children }: { children: any }) {
  const { setFilteredNFTs, setSelectedNFT } = useNFTContext();
  const { getRoomsByFilters, getSuggestedRooms } = useRoomContext();

  const [filters, setFilters] = useState<SearchFilter>({
    floorNumber: undefined,
    lockedState: undefined,
    visitorLimit: undefined,
    size: undefined,
    search: undefined,
  });

  const [searchTerm, setSearchTermRaw] = useState<string>('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [searchFetching, setSearchFetching] = useState(false);

  const [suggestedRooms, setSuggestedRooms] = useState<RoomObjectType[]>([]);

  const setSearchTerm = useCallback(
    (term: string) => {
      if (term === '') {
        setFilteredNFTs([]);
      }

      setSearchTermRaw(term);
    },
    [setFilteredNFTs]
  );

  const onSearch = useCallback(
    async (filter: SearchFilter, signal: AbortSignal) => {
      // setSelectedNFT(undefined); // not sure why this is here

      const copyFilter = { ...filter };

      for (const key in copyFilter) {
        if (!copyFilter[key as keyof SearchFilter]) {
          delete copyFilter[key as keyof SearchFilter];
        }
      }

      if (Object.keys(copyFilter).length < 1) {
        setFilteredNFTs([]);
        return [];
      }

      try {
        setSearchFetching(true);
        const rooms = await getRoomsByFilters(
          copyFilter,
          requiredAttributes,
          signal
        );

        setFilteredNFTs(rooms);
        setSearchFetching(false);
      } catch (err) {
        setSearchFetching(false);
        setFilteredNFTs([]);
      }
    },
    [getRoomsByFilters, setFilteredNFTs]
  );

  const isFiltered = useMemo(
    () =>
      !!debouncedSearchTerm || Object.values(filters).some((value) => !!value),
    [debouncedSearchTerm, filters]
  );

  useEffect(() => {
    if (isFiltered) {
      let controller = new AbortController();

      const filter = {
        ...filters,
        search: debouncedSearchTerm,
      };

      onSearch(filter, controller.signal);

      return () => {
        controller.abort();
        setSearchFetching(false);
      };
    } else {
      setSearchTerm('');
      setFilteredNFTs([]);
      setSearchFetching(false);
    }
  }, [
    debouncedSearchTerm,
    filters,
    isFiltered,
    onSearch,
    setFilteredNFTs,
    setSearchTerm,
    setSelectedNFT,
  ]);

  useEffect(() => {
    const abortController = new AbortController();
    getSuggestedRooms(requiredAttributes, abortController.signal)
      .then((rooms) => setSuggestedRooms(rooms))
      .catch((err) => console.log(err));
    return () => abortController.abort();
  }, [getSuggestedRooms]);

  return (
    <SearchContext.Provider
      value={{
        filters,
        searchTerm,
        isFiltered,
        setFilters,
        setSearchTerm,
        searchFetching,
        suggestedRooms,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('Search context must be used within a Provider');
  }
  return context;
}
